import { normalizeText } from "../../../utils/normalizeText";
// import { Icon } from '@iconify-icon/react/dist/iconify.mjs';
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./style.css";
import AppIcon from "../../App/AppIcon";

interface CustomSelectProps {
  value: string | string[] | undefined;
  onChange: (val: string | string[] | undefined) => void;
  options: { label: string; value: string; style?: React.CSSProperties }[];
  showClear?: boolean;
  showSearch?: boolean;
  className?: string;
  error?: string;
  disabled?: boolean;
  maxLabelLength?: number;
}

const CustomSelect: React.FC<CustomSelectProps> = ({ value, onChange, options, showClear, showSearch, className = "", error, disabled, maxLabelLength = 50 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, maxHeight: 0, openUpwards: false });
  const selectRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLUListElement>(null);

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const actualValue = Array.isArray(value) ? value[0] : value;
  const selectedOption = options.find((opt) => opt.value === actualValue);

  const handleSelect = (val: string) => {
    if (Array.isArray(value)) {
      onChange([val]);
    } else {
      onChange(val);
    }
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleClear = () => {
    onChange(undefined);
    setIsOpen(false);
    setSearchQuery("");
  };

  const filteredOptions = options.filter((opt) => normalizeText(opt.label).includes(normalizeText(searchQuery)));

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (selectRef.current && !selectRef.current.contains(target) && portalRef.current && !portalRef.current.contains(target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && selectRef.current) {
      const updatePosition = () => {
        const rect = selectRef.current?.getBoundingClientRect();
        if (rect) {
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - rect.bottom;
          const spaceAbove = rect.top;
          const dropdownHeight = 300;
          
          const openUpwards = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
          const maxHeight = openUpwards 
            ? Math.min(spaceAbove - 10, dropdownHeight)
            : Math.min(spaceBelow - 10, dropdownHeight);
          
          setDropdownPosition({
            top: openUpwards ? rect.top : rect.bottom,
            left: rect.left,
            width: rect.width,
            maxHeight,
            openUpwards
          });
        }
      };
      
      updatePosition();
      const timeoutId = setTimeout(updatePosition, 10);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  return (
    <div className={`custom-select-container ${className}`} ref={selectRef}>
      <div onClick={() => (disabled ? {} : setIsOpen(!isOpen))} className={`custom-select-trigger ${disabled ? "disabled" : ""}`}>
        <span className={actualValue ? "" : "custom-select-placeholder"}>
          {selectedOption ? truncateText(selectedOption.label, maxLabelLength) : "Selecione..."}
        </span>
        <div className="custom-select-actions">
          {showClear && actualValue && !disabled && (
            <button
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="custom-select-clear"
            >
              <AppIcon icon={"mdi:clear"} />X
            </button>
          )}
          <span>
            <AppIcon icon={"raphael:arrowdown"} width={12} />
          </span>
        </div>
      </div>

      {isOpen &&
        createPortal(
          <ul
            ref={portalRef}
            className={`custom-select-dropdown ${error ? "error" : ""} ${dropdownPosition.openUpwards ? "open-upwards" : ""}`}
            style={{
              position: "fixed",
              top: dropdownPosition.openUpwards ? "auto" : dropdownPosition.top,
              bottom: dropdownPosition.openUpwards ? window.innerHeight - dropdownPosition.top : "auto",
              left: dropdownPosition.left,
              width: dropdownPosition.width || "auto",
              maxHeight: dropdownPosition.maxHeight,
              overflowY: "auto",
            }}
          >
            {showSearch && (
              <li className="custom-select-search">
                <input type="text" placeholder="Buscar..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
              </li>
            )}

            {filteredOptions.map((opt) => (
              <li 
                key={opt.value} 
                onClick={() => handleSelect(opt.value)} 
                className={`custom-select-option ${opt.value === actualValue ? "selected" : ""} ${opt.value === "" ? "faded" : ""}`} 
                title={opt.label}
                style={opt.style}
              >
                {truncateText(opt.label, maxLabelLength)}
              </li>
            ))}

            {filteredOptions.length === 0 && <li className="custom-select-no-options">Nenhuma opção encontrada</li>}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default CustomSelect;
